import { ComponentProps, useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Label, Modal, ModalBody, ModalHeader, Row, Spinner, Input, FormGroup, InputGroup, InputGroupText } from "reactstrap";
import Swal from "sweetalert2";
import TextField from "../../../common/TextField";
import Dropdown from "../../../common/DropDown";
import { H3 } from "../../../../AbstractElements";
import { toast } from "react-toastify";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import api from "../../../../Hooks/axiosApi";
import {
  getCrmLeadsByIdsService,
  getCrmLoanEnquiryService,
  getAbnDetails,
  updateCrmLoanEnquiryService,
  getLoanEnquiryMatchService,
} from "../../../../Services/CRMAdminServices/CRMTaskListService";
import { AxiosResponse } from "axios";
import DollarInput from "../../../common/DollarInput";
import DecimalInput from "../../../common/DecimalInput";

const formatDateToDDMMYYYY = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseTermToMonths = (termStr: string): number => {
  if (!termStr) return 0;
  const cleanStr = termStr.trim().toLowerCase();
  const match = cleanStr.match(/^(\d+)/);
  if (!match) return 0;
  const num = parseInt(match[1], 10);
  
  if (cleanStr.includes("year") && !cleanStr.includes("(")) {
    return num * 12;
  }
  
  return num;
};

const mapToNumericMonths = (value: string): string => {
  if (!value) return "";
  const months = parseTermToMonths(value);
  return months > 0 ? String(months) : value;
};

const getEmptyLoanEnquiry = () => ({
  businessPurposeConfirm: "",
  abnConfirm: "",
  consentConfirm: "",
  truthfulnessConfirm: "",
  leadId: "",
  firstName: "",
  surname: "",
  mobile: "",
  email: "",
  fundingRequired: "",
  urgency: "",
  primaryPurpose: "",
  industry: "",
  tradingYears: "",
  propertySecurityAvailable: "",
  propertyAddress: "",
  estimatedValue: "",
  currentLoanBalance: "",
  lvr: "",
  propertyClass: "",
  propertySubType: "",
  assetClass: "",
  assetType: "",
  assetYear: "",
  balloonPayment: "",
  purchasePrice: "",
  deposit: "",
  assetSource: "",
  gstRegistered: "",
  gstFunded: "",
  productType: "",
  securityPosition: "",
  repaymentPlan: "",
  loanTerm: "",
  repaymentFrequency: "",
  repaymentType: "",
  creditProfile: "",
  defaults: "",
  totalDebt: "",
  atoArrangement: "",
  incomeVerification: "",
  bankStatementsMonths: "",
  existingLoans: "",
  borrowerStructure: "",
  abn: "",
  acn: "",
  gst: "",
  entityName: "",
  entityType: "",
  businessName: "",
  addressState: "",
  director1: "",
  director1Residency: "",
  director2: "",
  director2Residency: "",
  directorBankrupt: "",
  comments: "",
  monthlyTurnover: "",
  abnStatus: "",
  abnDate: "",
  ownProperty: "",
  homeValue: "",
  homeDebt: "",
  netEquity: "",
  residentialStatus: "",
  cashDeposit: "",
  tradeInValue: "",
  payoutAmount: "",
  preferredLoanTerm: "",
  assetSubClass: "",
  electricVehicle: "",
});

type LoanEnquiryFields = ReturnType<typeof getEmptyLoanEnquiry>;

interface LoanEnquiryProps {
  leadIDProp?: string;
  onClose?: () => void;
}

const urgencyOptions = ["24-48 Hours", "1 week", "2 - 4 weeks"];
const primaryPurposeOptions = [
  "Business Cashflow",
  "Construction Loan",
  "Property Purchase",
  "Property Refinance",
  "Equipment Purchase",
  "Vehicle Purchase",
];
const industryOptions = [
  "Accounting",
  "Adult Industry",
  "Aged Care",
  "Agriculture and Farming",
  "Alcohol",
  "Aviation",
  "Automotive",
  "Beauty",
  "Builders",
  "Cannabis",
  "Coal Mining",
  "Construction",
  "Consulting",
  "Cryptocurrency",
  "Debt Collection",
  "Defence",
  "Drilling and Exploration",
  "Education and Training",
  "Energy Production",
  "Financial Services",
  "Fishing and Aquaculture",
  "Forestry and Logging",
  "Freight and Shipping",
  "Gambling",
  "Hairdressing",
  "Healthcare",
  "Hospitality and Nightclubs",
  "Insurance",
  "Legal Services",
  "Manufacturing",
  "Marketing",
  "Media and Entertainment",
  "Mining Services",
  "Oil and Gas",
  "Passenger Transport",
  "Pawnbrokers",
  "Petrol",
  "Pharmaceutical",
  "Real Estate",
  "Property Development",
  "Recycling",
  "Renewable Energy",
  "Restaurants",
  "Retail and E commerce",
  "Retirement",
  "Rideshare Uber / Taxi",
  "Security Services",
  "Tattoo Studios",
  "Taxi / Chauffer",
  "Technology and Software",
  "Telecommunications",
  "Tobacco",
  "Transport and Logistics",
  "Waste Management",
].sort();
const tradingYearsOptions = ["Under 6 months", "6–12 months", "1–2 years", "2+ years"];
const propertySecurityOptions = [
  "Secured - Property",
  "Secured - Asset/Equipment",
  "Unsecured - Cashflow loan",
];
const propertyClassOptions = ["Residential", "Commercial", "Rural"];
const residentialSubtypes = [
  "Apartment",
  "Co Living",
  "Dual Occupancy",
  "House Established",
  "NDIS SDA Housing",
  "Residential Land",
  "Rooming House",
  "Short Stay",
  "Townhouse",
  "Unit",
  "Villa",
].sort();
const commercialSubtypes = [
  "Car Dealership",
  "Childcare / Education",
  "Commercial Land",
  "Factory",
  "Hospitality",
  "Industrial Warehouse",
  "Medical Clinic",
  "Office",
  "Retail Complex",
  "Retail Shop",
  "Service Station",
].sort();
const ruralSubtypes = [
  "Agricultural Processing",
  "Aquaculture / Fish",
  "Dairy Farm",
  "Forestry / Timber",
  "Grain",
  "Livestock",
  "Mixed Farming",
  "Orchard / Vineyard",
  "Rural Land",
  "Rural Lifestyle / Hobby",
].sort();
const propertySubTypeOptions = [
  ...residentialSubtypes,
  ...commercialSubtypes,
  ...ruralSubtypes,
];
const assetTypeOptions = [
  "Air conditioning units - Other",
  "Amusement machines - Other",
  "Amusement rides - Other",
  "Backhoe - Yellow Goods",
  "Backhoe loader - Construction",
  "Balers - Agricultural",
  "Bobcat - Construction",
  "Boom lift - Construction",
  "Bulldozer - Yellow Goods",
  "Bus - Heavy Vehicle more than 4.5 Ton",
  "Combine harvester - Agricultural",
  "Commercial vessel - Other",
  "Computers - Other",
  "Crane - Heavy Vehicle more than 4.5 Ton",
  "Cultivators - Agricultural",
  "Drum press - Other",
  "Dump truck - Construction",
  "Excavator - Yellow Goods",
  "Fishing vessel - Other",
  "Floor coverings - Other",
  "Forklift - Construction",
  "Forage harvester - Agricultural",
  "Fuel tanks - Other",
  "Grader - Yellow Goods",
  "Harvester - Agricultural",
  "Hatch - Motor Vehicle less than 4.5 Ton",
  "Irrigation equipment - Agricultural",
  "Loader - Yellow Goods",
  "Mini truck - Motor Vehicle less than 4.5 Ton",
  "POS systems - Other",
  "Ploughs - Agricultural",
  "Prime mover - Heavy Vehicle more than 4.5 Ton",
  "Rigid truck - Heavy Vehicle more than 4.5 Ton",
  "Roller or compactor - Construction",
  "Scaffolding - Construction",
  "Scissor lift - Construction",
  "Seed drills - Agricultural",
  "Sedan - Motor Vehicle less than 4.5 Ton",
  "Signage - Other",
  "Slashers - Agricultural",
  "Solar equipment - Other",
  "Spray booths - Other",
  "Sprayers - Agricultural",
  "SUV - Motor Vehicle less than 4.5 Ton",
  "Telehandler - Construction",
  "Tractor - Agricultural",
  "Trailer - Heavy Vehicle more than 4.5 Ton",
  "Trailers agricultural - Agricultural",
  "Truck mounted crane - Construction",
  "Ute - Motor Vehicle less than 4.5 Ton",
  "Van - Motor Vehicle less than 4.5 Ton",
  "Wheel loader - Construction",
  "Other not known - Other",
];

const assetSubClassMap = {
  "Motor Vehicle <4.5 Ton": ["Mini trucks", "Sedan", "Utes", "Vans"].sort(),
  "Heavy Vehicle >4.5 Ton": ["Bus", "Crane", "Prime Mover", "Rigid Truck (>4.5t)", "Trailer (Truck)"].sort(),
  "Agricultural": ["Balers", "Combine harvesters", "Cultivators", "Fertiliser spreaders", "Forage harvesters", "Irrigation equipment", "Ploughs", "Seed drills", "Slashers", "Sprayers", "Tractors", "Trailers"].sort(),
  "Yellow Goods": ["Excavators", "Bulldozers", "Loaders", "Backhoes", "Graders", "Dump trucks"].sort(),
  "Construction": ["Forklift", "Telehandler", "Bobcat", "Excavator", "Backhoe loader", "Wheel loader", "Bulldozer", "Grader", "Roller / compactor", "Dump truck", "Truck-mounted crane", "Scissor lift", "Scaffolding", "Boom lift"].sort(),
  "Other": ["Air Conditioning Units", "Amusement machines", "Amusement Rides", "Commercial vessel", "Computers", "Drum Press", "Fishing vessel", "Floor coverings", "Fuel Tanks", "POS Systems", "Signage", "Solar Equipment", "Spray Booths", "Other - Not Known"].sort()
};

const assetLoanTermOptions = [
  { value: "1", label: "1" },
  { value: "3", label: "3" },
  { value: "6", label: "6" },
  { value: "12", label: "12 (1 year)" },
  { value: "24", label: "24 (2 year)" },
  { value: "36", label: "36 (3 year)" },
  { value: "48", label: "48 (4 year)" },
  { value: "60", label: "60 (5 years)" }
];

const balloonResidualOptions = ["10%", "20%", "30%", "40% max"];
const electricVehicleOptions = ["No", "Full Electric", "Hybrid"];
const assetSourceOptions = ["Dealer", "Private"];
const gstOptions = ["Yes", "No"];
const repaymentPlanOptions = ["Asset sale", "Business cashflow", "Property sale", "Refinance"].sort();
const repaymentFrequencyOptions = ["Daily", "Weekly", "Fortnightly", "Monthly"];
const loanTermOptions = [
  { value: "1", label: "1" },
  { value: "3", label: "3" },
  { value: "6", label: "6" },
  { value: "12", label: "12 (1 year)" },
  { value: "24", label: "24 (2 year)" },
  { value: "36", label: "36 (3 year)" },
  { value: "48", label: "48 (4 year)" },
  { value: "60", label: "60 (5 years)" }
];
const repaymentTypeOptions = ["Interest Only", "Principal & Interest"];
const creditProfileOptions = [
  "Good Credit",
  "Late Payments",
  "Default Paid",
  "Default Unpaid",
  "Judgement",
  "Bankruptcy",
];
const atoArrangementOptions = ["Not Applicable", "Yes", "No"];
const defaultCountOptions = ["None", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const incomeVerificationOptions = [
  "BAS Statements",
  "Bank Statements",
  "Full Financials",
  "No Financials - Accountant Letter",
  "No Financials - No Accountant Letter",
].sort();
const bankStatementOptions = ["None", "3 months", "6 months", "12 months"];
const borrowerStructureOptions = ["ABN Partnership", "ABN Sole trader", "Company", "Trust"].sort();
const securityPositionOptions = ["1st Mortgage", "2nd Mortgage"];
const productTypeOptions = ["Property", "Asset Finance", "Working Capital", "Equipment"];

const ownPropertyOptions = ["Yes", "No"];
const residentialStatusOptions = [
  "Boarder",
  "Living with Parents",
  "Renting - Agent",
  "Renting - Private",
];
const residencyStatusOptions = ["Australian Citizen", "Permanent Resident", "Temporary Visa", "Foreign Resident"];

const currencyFields = [
  "fundingRequired",
  "estimatedValue",
  "currentLoanBalance",
  "purchasePrice",
  "deposit",
  "totalDebt",
  "existingLoans",
  "monthlyTurnover",
  "homeValue",
  "homeDebt",
  "netEquity",
  "cashDeposit",
  "tradeInValue",
  "payoutAmount",
  "preferredLoanTerm",
] as const;
type CurrencyField = (typeof currencyFields)[number];

const percentFields = ["lvr", "balloonPayment"] as const;
type PercentField = (typeof percentFields)[number];

const cleanForApi = (value: string) => {
  if (!value) return value;
  const trimmed = value.replace(/[$\s%]/g, "");
  if (!trimmed) return trimmed;
  const cleaned = trimmed.replace(/,/g, "");
  return cleaned;
};

const normalizeLoanData = (payload: any): LoanEnquiryFields => {
  const normalized = getEmptyLoanEnquiry();
  (Object.keys(normalized) as Array<keyof LoanEnquiryFields>).forEach((key) => {
    const value = payload?.[key];
    normalized[key] = value === undefined || value === null ? "" : `${value}`;
  });

  if (normalized.urgency === "ASAP") {
    normalized.urgency = "24-48 Hours";
  }

  // Extract Asset Source from comments if present
  let commentsCleaned = normalized.comments || "";
  if (commentsCleaned) {
    const assetSourceRegex = /(?:Asset Source|Asset source):\s*(Dealer|Private)/i;
    const match = commentsCleaned.match(assetSourceRegex);
    if (match) {
      if (!normalized.assetSource) {
        normalized.assetSource = match[1];
      }
      commentsCleaned = commentsCleaned.replace(assetSourceRegex, "").trim();
    }
  }
  normalized.comments = commentsCleaned;

  // Normalize loan terms to clean numeric month strings
  normalized.loanTerm = mapToNumericMonths(normalized.loanTerm);
  normalized.preferredLoanTerm = mapToNumericMonths(normalized.preferredLoanTerm);

  return normalized;
};

const ensureStringValue = (value: any) => (value === null || value === undefined ? "" : `${value}`);

const applyLeadFallback = (current: LoanEnquiryFields, lead?: any) => {
  if (!lead) return current;
  const updates: Partial<LoanEnquiryFields> = {};
  const setIfEmpty = (key: keyof LoanEnquiryFields, sourceValue: string) => {
    if (!current[key] && sourceValue) {
      updates[key] = sourceValue;
    }
  };

  setIfEmpty("firstName", ensureStringValue(lead.firstName));
  setIfEmpty("surname", ensureStringValue(lead.surname));
  setIfEmpty("email", ensureStringValue(lead.email));
  setIfEmpty("mobile", ensureStringValue(lead.mobile ?? lead.mobileNo));

  if (!Object.keys(updates).length) {
    return current;
  }

  return {
    ...current,
    ...updates,
  };
};

const formatBusinessName = (input: unknown) => {
  if (!input) return "";
  if (Array.isArray(input)) {
    return input.filter(Boolean).join(", ");
  }
  return `${input}`;
};

interface AbnDetailsPayload {
  abn?: string;
  abnStatus?: string;
  abnStatusEffectiveFrom?: string;
  acn?: string;
  addressDate?: string;
  addressPostcode?: string;
  addressState?: string;
  businessName?: string[] | string;
  entityName?: string;
  entityTypeCode?: string;
  entityTypeName?: string;
  gst?: string;
  message?: string;
}

type LoanFieldProps = ComponentProps<typeof TextField>;
const LoanField = (props: LoanFieldProps) => (
  <TextField {...props} design={props.design ?? "flex"} passCol={props.passCol ?? "flex-grow-1"} />
);

type LoanCurrencyFieldProps = ComponentProps<typeof DollarInput>;
const LoanCurrencyField = (props: LoanCurrencyFieldProps) => (
  <DollarInput {...props} design={props.design ?? "flex"} passCol={props.passCol ?? "flex-grow-1"} />
);

type LoanPercentFieldProps = ComponentProps<typeof DecimalInput>;
const LoanPercentField = (props: LoanPercentFieldProps) => (
  <DecimalInput {...props} design={props.design ?? "flex"} passCol={props.passCol ?? "flex-grow-1"} />
);

type LoanIntegerFieldProps = ComponentProps<typeof TextField> & { suffix?: string };
const LoanIntegerField = (props: LoanIntegerFieldProps) => {
  const design = props.design ?? "flex";
  const passCol = props.passCol ?? "flex-grow-1";
  return (
    <FormGroup className={design === "flex" ? "d-flex align-items-center mb-0" : "mb-0"}>
      {props.label && (
        <Label for={props.name} className={design === "flex" ? "col-3 mb-0" : "mb-0"}>
          {props.label}
        </Label>
      )}
      <div className={passCol}>
        <InputGroup>
          <Input
            id={props.name}
            name={props.name}
            type={props.type ?? "text"}
            value={props.value}
            onChange={(e) => {
              let val = e.target.value;
              if (props.type === "number") {
                val = val.replace(/[^0-9]/g, "");
                if (props.maxChars && val.length > props.maxChars) return;
              }
              props.setValue(props.name, val);
            }}
            placeholder={props.placeholder}
            tabIndex={props.tabIndex}
            style={props.style}
            disabled={props.disabled}
          />
          {props.suffix && <InputGroupText>{props.suffix}</InputGroupText>}
        </InputGroup>
      </div>
    </FormGroup>
  );
};

type LoanDropdownProps = ComponentProps<typeof Dropdown>;
const LoanDropdown = (props: LoanDropdownProps) => (
  <Dropdown {...props} design={props.design ?? "flex"} passCol={props.passCol ?? "flex-grow-1"} />
);

const sectionTitleStyle = {
  color: "blue",
  fontWeight: "bold",
  fontSize: "16px",
  marginBottom: "10px",
};

const sectionSeparatorStyle = {
  height: "2px",
  backgroundColor: "blue",
  width: "100%",
  marginTop: "20px",
  marginBottom: "10px",
};

const contentWrapperStyle = {
  width: "100%",
};

const primaryButtonStyle = {
  backgroundColor: "#0000ff",
  borderColor: "#0000ff",
  color: "#fff",
};

const rowClass = "gy-1 align-items-center";



function LoanEnquiry({ leadIDProp, onClose }: LoanEnquiryProps) {
  const navigate = useSafeNavigate();
  const { leadID } = useParams<{ leadID: string }>();
  const [loanData, setLoanData] = useState<LoanEnquiryFields>(getEmptyLoanEnquiry());
  const [abnLookupMessage, setAbnLookupMessage] = useState("");
  const [abnLookupError, setAbnLookupError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const isInitialMount = useRef(true);
  const [lenders, setLenders] = useState<any[]>([]);

  useEffect(() => {
    const fetchLenders = async () => {
      try {
        const response = await api.get("/api/Lenders/Getlenders");
        const apiData = response?.data;
        const list = Array.isArray(apiData) ? apiData : [];
        setLenders(list);
      } catch (error) {
        console.error("Failed to fetch lenders", error);
      }
    };
    fetchLenders();
  }, []);

  const fetchAddressSuggestions = async (query: string) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const url = `https://api.addressfinder.io/api/au/address/autocomplete?q=${encodeURIComponent(query)}&key=Y4KUGQHWNVB7ARMCF6DP&format=json`;
      const response = await fetch(url);
      const data = await response.json();
      setAddressSuggestions(data.completions || []);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
    }
  };

  const resolvedLeadId = leadIDProp || leadID;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetRequestQuery(
    ["getCrmLoanEnquiry", resolvedLeadId],
    () => getCrmLoanEnquiryService(resolvedLeadId!),
    { enabled: !!resolvedLeadId }
  );

  const { data: leadDetails } = useGetRequestQuery(
    ["getCrmLeadById", resolvedLeadId],
    () => getCrmLeadsByIdsService(resolvedLeadId!),
    { enabled: !!resolvedLeadId }
  );

  const { mutate: lookupAbn, isPending: isAbnLookupPending } = useMutationRequestQuery<
    AxiosResponse<AbnDetailsPayload>,
    unknown,
    string
  >("lookupAbn", (abn: string) => getAbnDetails(abn), {
    onSuccess: (response) => {
      const payload = response?.data || {};
      if (!payload.abn) {
        toast.error("ABN lookup returned no details.");
        setAbnLookupError("No ABN details found.");
        setAbnLookupMessage("");
        return;
      }
      setLoanData((prev) => ({
        ...prev,
        abn: payload.abn || prev.abn,
        entityName: payload.entityName || prev.entityName,
        entityType: payload.entityTypeName || prev.entityType,
        gst: payload.gst || prev.gst,
        addressState: payload.addressState || prev.addressState,
        businessName: formatBusinessName(payload.businessName) || prev.businessName,
        abnStatus: payload.abnStatus || prev.abnStatus,
        abnDate: payload.abnStatusEffectiveFrom || prev.abnDate,
      }));

      // Automatically calculate trading years from abnDate (abnStatusEffectiveFrom)
      if (payload.abnStatusEffectiveFrom) {
        const startDate = new Date(payload.abnStatusEffectiveFrom);
        const today = new Date();
        const diffMonths = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());
        
        let yearsTrading = "Under 6 months";
        if (diffMonths >= 24) yearsTrading = "2+ years";
        else if (diffMonths >= 12) yearsTrading = "1–2 years";
        else if (diffMonths >= 6) yearsTrading = "6–12 months";
        
        setLoanData(prev => ({ ...prev, tradingYears: yearsTrading }));
      }

      setAbnLookupMessage(`ABN ${payload.abn} (${payload.abnStatus || "Unknown"})`);
      setAbnLookupError("");
    },
    onError: () => {
      toast.error("Failed to lookup ABN.");
      setAbnLookupError("ABN lookup failed.");
      setAbnLookupMessage("");
    },
  });

  const assetYears = useMemo(() => {
    const years: string[] = [];
    const currentYear = new Date().getFullYear();
    for (let year = currentYear; year >= 1990; year -= 1) {
      years.push(year.toString());
    }
    return years;
  }, []);

  useEffect(() => {
    if (!data) return;
    const payload = data?.data || data;
    if (!payload) return;
    setLoanData(normalizeLoanData(payload));
    // After initial load, give a small window before enabling autosave
    setTimeout(() => {
      isInitialMount.current = false;
    }, 1000);
  }, [data]);

  useEffect(() => {
    if (!leadDetails) return;
    const payload = leadDetails?.data || leadDetails;
    if (!payload) return;
    setLoanData((prev) => applyLeadFallback(prev, payload));
  }, [leadDetails, data]);

  const handleLookupAbn = () => {
    const sanitized = (loanData.abn || "").replace(/[^0-9]/g, "");
    if (!sanitized) {
      toast.warn("Please enter an ABN before looking up.");
      return;
    }
    setAbnLookupMessage("");
    setAbnLookupError("");
    lookupAbn(sanitized);
  };

  const handleFieldChange = (name: string, value: string) => {
    setLoanData((prev) => {
      const updated = { ...prev, [name]: value };

      // Automatically calculate LVR when estimated value or current loan balance changes
      if (name === "estimatedValue" || name === "currentLoanBalance") {
        const ev = parseFloat(cleanForApi(updated.estimatedValue) || "0");
        const clb = parseFloat(cleanForApi(updated.currentLoanBalance) || "0");

        if (ev > 0) {
          const lvrValue = (clb / ev) * 100;
          updated.lvr = lvrValue.toFixed(2);
        } else {
          updated.lvr = "";
        }
      }

      // Automatically calculate Net Equity and LVR when Home Value or Home Debt changes
      if (name === "homeValue" || name === "homeDebt") {
        const worth = parseFloat(cleanForApi(updated.homeValue) || "0");
        const debt = parseFloat(cleanForApi(updated.homeDebt) || "0");
        updated.netEquity = (worth - debt).toString();
        
        if (worth > 0) {
          const lvrValue = (debt / worth) * 100;
          updated.lvr = Math.min(lvrValue, 99).toFixed(2);
        } else {
          updated.lvr = "";
        }
      }

      return updated;
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const containerClass = "container-fluid py-4 bg-white";

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }

    if (resolvedLeadId) {
      navigate(`${process.env.PUBLIC_URL}/EditLead/${resolvedLeadId}`);
    } else {
      navigate('/CRMTaskList');
    }
  };

  const hasLeadId = Boolean(resolvedLeadId);
  type LoanEnquiryPayload = {
    [K in Exclude<keyof LoanEnquiryFields, "leadId">]: string;
  } & { leadId: number };
  
  const handleSave = async (isSilentArg: any = false) => {
    const isSilent = typeof isSilentArg === 'boolean' ? isSilentArg : false;
    
    if (!hasLeadId) {
      if (!isSilent) toast.warn("Lead ID is required before saving.");
      return;
    }
    
    if (isSilent) setIsAutosaving(true);
    else setIsSaving(true);

    const saveData = { ...loanData };
    if (saveData.assetSource) {
      const assetSourceLine = `Asset Source: ${saveData.assetSource}`;
      if (saveData.comments) {
        saveData.comments = `${saveData.comments.trim()}\n\n${assetSourceLine}`;
      } else {
        saveData.comments = assetSourceLine;
      }
    }

    try {
      await updateCrmLoanEnquiryService(saveData);
      
      if (!isSilent) {
        toast.success("Loan enquiry updated.");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2500);
      }
    } catch (error) {
      if (!isSilent) {
        console.error("Loan enquiry update failed", error);
        toast.error("Failed to update the loan enquiry.");
      }
    } finally {
      setIsSaving(false);
      setIsAutosaving(false);
    }
  };

  // Autosave effect
  useEffect(() => {
    if (isInitialMount.current || isLoading) return;

    const debounceTimer = setTimeout(() => {
      handleSave(true);
    }, 2000); // 2 second delay

    return () => clearTimeout(debounceTimer);
  }, [loanData]);

  const validateForm = () => {
    const requiredFields = [
      { key: "firstName", label: "First Name" },
      { key: "surname", label: "Surname" },
      { key: "mobile", label: "Mobile" },
      { key: "email", label: "Email" },
      { key: "fundingRequired", label: "Funding Required" },
      { key: "primaryPurpose", label: "Primary Purpose" },
      { key: "industry", label: "Industry" },
      { key: "tradingYears", label: "How long trading" },
    ];

    const missingFields = requiredFields
      .filter((field) => !loanData[field.key as keyof LoanEnquiryFields] || String(loanData[field.key as keyof LoanEnquiryFields]).trim() === "")
      .map((field) => field.label);

    const newErrors: Record<string, boolean> = {};
    requiredFields.forEach(f => {
      if (!loanData[f.key as keyof LoanEnquiryFields] || String(loanData[f.key as keyof LoanEnquiryFields]).trim() === "") {
        newErrors[f.key] = true;
      }
    });

    setFormErrors(newErrors);
    return missingFields;
  };

  const handleViewResults = async () => {
    if (!hasLeadId) {
      toast.warn("Lead ID is required.");
      return;
    }

    const missing = validateForm();
    if (missing.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Loan Enquiry",
        html: `Please complete the following required fields before matching:<br/><br/>
               <ul style="text-align: left;">${missing.map(f => `<li>${f}</li>`).join('')}</ul>`,
        confirmButtonColor: "#0000ff"
      });
      return;
    }

    let productTypeStr = "";
    switch (loanData.propertySecurityAvailable) {
      case "Secured - Asset/Equipment":
        productTypeStr = "asset-secured";
        break;
      case "Unsecured - Cashflow loan":
        productTypeStr = "asset-unsecured";
        break;
      case "Secured - Property":
        productTypeStr = "property";
        break;
    }

    if (!productTypeStr) {
      Swal.fire({
        icon: "warning",
        title: "Product Type Required",
        text: "Please select a valid property security option before matching.",
        confirmButtonColor: "#0000ff"
      });
      return;
    }

    // 1. Save data first to ensure match is accurate
    await handleSave();

    // 2. Open new tab
    const publicUrl = process.env.PUBLIC_URL || "";
    window.open(`${publicUrl}/CRMLoanEnquiryResults/${resolvedLeadId}`, '_blank');
  };

  return (
    <div className={containerClass}>
      {/* <div className="text-center mb-4">
        <img src="https://fintrack.com.au/img/new-logo.png" alt="Fintrack Logo" style={{ maxWidth: '200px' }} />
      </div> */}
      <div className="px-3" style={contentWrapperStyle}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <H3 className="heading-color f-w-900 mb-1" style={{ color: '#0000ff' }}>
              LOAN ENQUIRY FORM &nbsp;&nbsp;&nbsp; Lead ID: {resolvedLeadId || "Not set"}
              {isAutosaving && <small className="ms-3 text-muted" style={{ fontSize: '12px', fontWeight: 'normal' }}> (Saving...)</small>}
            </H3>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              color="primary"
              size="sm"
              style={primaryButtonStyle}
              disabled={isLoading || !hasLeadId}
              onClick={() => refetch()}
            >
              {isLoading ? "Refreshing..." : "Refresh data"}
            </Button>
            <Button color="primary" size="sm" style={primaryButtonStyle} disabled={isSaving || !hasLeadId} onClick={handleSave}>
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
            <Button
              color="success"
              size="sm"
              className="fw-bold"
              disabled={isLoading || !hasLeadId}
              onClick={handleViewResults}
            >
              View Results
            </Button>
            <Button color="primary" size="sm" style={primaryButtonStyle} onClick={handleBack}>
              Back to lead
            </Button>
          </div>
        </div>

        {saveSuccess && (
          <Alert color="success" className="mb-3 px-3">
            Loan enquiry saved successfully.
          </Alert>
        )}

        {!hasLeadId && (
          <Alert color="warning">
            A lead identifier is required to load the loan enquiry. Please open this page from a lead record.
          </Alert>
        )}

        {isError && (
          <Alert color="danger" className="mb-3">
            Failed to load loan enquiry: {error?.message || "Unknown error"}
          </Alert>
        )}

        {isLoading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading enquiry from the API…</div>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {/* <div className="sec01 mb-4 p-3 rounded" style={{ backgroundColor: '#fff', border: '1px solid #eee' }}>
              <p style={{ color: 'green', fontSize: '1.3rem', marginBottom: '15px' }}>
                We collect information to assess your business funding request. All calls are recorded for compliance purposes.
              </p>
              <div className="d-flex flex-column gap-2 mb-3">
                <style>{`
                  .custom-check-bullet {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    margin-bottom: 5px;
                  }
                  .custom-check-bullet .bullet-icon {
                    color: green;
                    font-weight: bold;
                    flex-shrink: 0;
                    margin-top: 2px;
                  }
                  .custom-check-bullet label {
                    margin-bottom: 0;
                    font-size: 14px;
                    line-height: 1.4;
                  }
                `}</style>
                <div className="custom-check-bullet">
                  <span className="bullet-icon">•</span>
                  <label>
                    You confirm this loan enquiry is for 51% or more for business or investment purposes.
                  </label>
                </div>
                <div className="custom-check-bullet">
                  <span className="bullet-icon">•</span>
                  <label>
                    You confirm you will be borrowing in a Company name or hold an Australian Business Number (ABN)
                  </label>
                </div>
                <div className="custom-check-bullet">
                  <span className="bullet-icon">•</span>
                  <label>
                    You consent to us contacting you and any calls being recorded for compliance purposes
                  </label>
                </div>
                <div className="custom-check-bullet">
                  <span className="bullet-icon">•</span>
                  <label>
                    You confirm information provided is true and correct to the best of your knowledge
                  </label>
                </div>
              </div>
            </div> */}

            <div style={sectionSeparatorStyle} />
            <h5 style={sectionTitleStyle}>YOUR DETAILS</h5>
            <Row className={rowClass}>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">First Name</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="firstName"
                    type="text"
                    value={loanData.firstName}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    disabled={true}
                    error={formErrors.firstName}
                    errorMessage="First name is required"
                    required
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Surname</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="surname"
                    type="text"
                    value={loanData.surname}
                    setValue={handleFieldChange}
                    style={{ textTransform: 'uppercase' }}
                    passCol="col-12"
                    disabled={true}
                    error={formErrors.surname}
                    errorMessage="Surname is required"
                    required
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Mobile</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="mobile"
                    type="text"
                    value={loanData.mobile}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    disabled={true}
                    error={formErrors.mobile}
                    errorMessage="Mobile is required"
                    required
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Email</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="email"
                    type="email"
                    value={loanData.email}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    disabled={true}
                    error={formErrors.email}
                    errorMessage="Email is required"
                    required
                  />
                </div>
              </Col>
            </Row>

            <div style={sectionSeparatorStyle} />
            <h5 style={sectionTitleStyle}>BORROWER DETAILS</h5>
            <Row className={rowClass}>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">What is your ABN?</Label>
                <div className="col-sm-9 d-flex align-items-center gap-2">
                  <LoanField
                    label=""
                    name="abn"
                    type="text"
                    value={loanData.abn}
                    setValue={handleFieldChange}
                    passCol="flex-grow-1"
                    style={{ marginRight: '5px' }}
                    maxChars={11}
                  />
                  <Button
                    color="primary"
                    size="sm"
                    style={{ ...primaryButtonStyle, padding: '2px 8px', fontSize: '14px', height: '35px', whiteSpace: 'nowrap', marginBottom: '15px' }}
                    onClick={handleLookupAbn}
                    disabled={isAbnLookupPending || !loanData.abn}
                  >
                    Find Your Business
                  </Button>
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">ABN Status</Label>
                <div className="col-sm-3">
                  <LoanField
                    label=""
                    name="abnStatus"
                    type="text"
                    value={loanData.abnStatus}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    disabled={true}
                  />
                </div>
                <Label className="col-sm-1 mb-0 text-center">DATE</Label>
                <div className="col-sm-2">
                  <LoanField
                    label=""
                    name="abnDate"
                    type="text"
                    value={formatDateToDDMMYYYY(loanData.abnDate)}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    disabled={true}
                  />
                </div>
              </Col>

              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">GST</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="gst"
                    type="text"
                    value={formatDateToDDMMYYYY(loanData.gst)}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Entity name</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="entityName"
                    type="text"
                    value={loanData.entityName}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Entity type</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="entityType"
                    type="text"
                    value={loanData.entityType}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Business name</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="businessName"
                    type="text"
                    value={loanData.businessName}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Address state</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="addressState"
                    type="text"
                    value={loanData.addressState}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Industry you are in?</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="industry"
                    value={loanData.industry}
                    setValue={handleFieldChange}
                    options={industryOptions}
                    passCol="col-12"
                    error={formErrors.industry}
                    errorMessage="Industry is required"
                    required
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Years Trading</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="tradingYears"
                    value={loanData.tradingYears}
                    setValue={handleFieldChange}
                    options={tradingYearsOptions}
                    passCol="col-12"
                    error={formErrors.tradingYears}
                    errorMessage="Trading years required"
                    required
                  />
                </div>
              </Col>

            </Row>

            <div style={sectionSeparatorStyle} />
            <h5 style={sectionTitleStyle}>HOME OWNERSHIP</h5>
            <Row className={rowClass}>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Do you own property?</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="ownProperty"
                    value={loanData.ownProperty}
                    setValue={handleFieldChange}
                    options={ownPropertyOptions}
                    passCol="col-12"
                  />
                </div>
              </Col>

              {loanData.ownProperty === "Yes" && (
                <>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">What is your Home worth?</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="homeValue"
                        value={loanData.homeValue}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">How much is your current debt on Home?</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="homeDebt"
                        value={loanData.homeDebt}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Net Equity</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="netEquity"
                        value={loanData.netEquity}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        disabled={true}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">LVR</Label>
                    <div className="col-sm-6">
                      <LoanPercentField
                        label=""
                        name="lvr"
                        value={loanData.lvr}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={5}
                        disabled={true}
                      />
                    </div>
                  </Col>
                </>
              )}

              {loanData.ownProperty === "No" && (
                <Col md={12} className="mb-1 d-flex align-items-center">
                  <Label className="col-sm-3 mb-0">Residential Status</Label>
                  <div className="col-sm-6">
                    <LoanDropdown
                      label=""
                      name="residentialStatus"
                      value={loanData.residentialStatus}
                      setValue={handleFieldChange}
                      options={residentialStatusOptions}
                      passCol="col-12"
                    />
                  </div>
                </Col>
              )}
            </Row>

            <div style={sectionSeparatorStyle} />
            <h5 style={sectionTitleStyle}>BORROWER / KEY PERSON PROFILE</h5>
            <Row className={rowClass}>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Credit Profile</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="creditProfile"
                    value={loanData.creditProfile}
                    setValue={handleFieldChange}
                    options={creditProfileOptions}
                    passCol="col-12"
                  />
                </div>
              </Col>

              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Total ATO Debt Outstanding</Label>
                <div className="col-sm-6">
                  <LoanCurrencyField
                    label=""
                    name="totalDebt"
                    value={loanData.totalDebt}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    limit={12}
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Are you in an ATO Arrangement Plan?</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="atoArrangement"
                    value={loanData.atoArrangement}
                    setValue={handleFieldChange}
                    options={atoArrangementOptions}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Income Verification Available</Label>
                <div className="col-sm-9 d-flex gap-3 flex-wrap">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="incomeVerification"
                      id="fullFinancials"
                      value="Full Doc - Financials"
                      checked={loanData.incomeVerification === "Full Doc - Financials"}
                      onChange={(e) => handleFieldChange("incomeVerification", e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="fullFinancials">Full Doc - Financials</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="incomeVerification"
                      id="lowDoc"
                      value="Low Doc - Accountant Letter"
                      checked={loanData.incomeVerification === "Low Doc - Accountant Letter"}
                      onChange={(e) => handleFieldChange("incomeVerification", e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="lowDoc">Low Doc - Accountant Letter</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="incomeVerification"
                      id="liteDoc"
                      value="Lite Doc - BAS & Bank Statements"
                      checked={loanData.incomeVerification === "Lite Doc - BAS & Bank Statements"}
                      onChange={(e) => handleFieldChange("incomeVerification", e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="liteDoc">Lite Doc - BAS & Bank Statements</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="incomeVerification"
                      id="noDoc"
                      value="No Doc"
                      checked={loanData.incomeVerification === "No Doc"}
                      onChange={(e) => handleFieldChange("incomeVerification", e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="noDoc">No Doc</label>
                  </div>
                </div>
              </Col>
            </Row>

            <div style={sectionSeparatorStyle} />
            <h5 style={sectionTitleStyle}>LOAN SNAPSHOT</h5>
            <Row className={rowClass}>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Funding Required</Label>
                <div className="col-sm-6">
                  <LoanCurrencyField
                    label=""
                    name="fundingRequired"
                    value={loanData.fundingRequired}
                    setValue={handleFieldChange}
                    passCol="col-12"
                    limit={12}
                    error={formErrors.fundingRequired}
                    errorMessage="Funding required is mandatory"
                    required
                  />
                </div>
              </Col>
              {/* <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Urgency</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="urgency"
                    value={loanData.urgency}
                    setValue={handleFieldChange}
                    options={urgencyOptions}
                    passCol="col-12"
                    error={formErrors.urgency}
                    errorMessage="Urgency is required"
                    required
                  />
                </div>
              </Col> */}
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Primary Purpose</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="primaryPurpose"
                    value={loanData.primaryPurpose}
                    setValue={handleFieldChange}
                    options={primaryPurposeOptions}
                    passCol="col-12"
                    error={formErrors.primaryPurpose}
                    errorMessage="Primary purpose is required"
                    required
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">What will be the security for this loan?</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="propertySecurityAvailable"
                    value={loanData.propertySecurityAvailable}
                    setValue={handleFieldChange}
                    options={propertySecurityOptions}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-3">
                <div style={{ color: "#00a651", marginLeft: "10px", fontWeight: "bold", fontSize: "1.6rem", backgroundColor: '#f0fff4', padding: '10px', borderRadius: '5px', border: '1px solid #c6f6d5' }}>
                  Using real estate as security for your loan lowers the interest rate and qualifies you for better loans.
                </div>
              </Col>
            </Row>

            {loanData.propertySecurityAvailable === "Secured - Property" && (
              <>
                <div style={sectionSeparatorStyle} />
                <h5 style={sectionTitleStyle}>SECURED - PROPERTY</h5>
                <p style={{ color: "#0000ff", fontSize: "0.85rem", marginLeft: "10px", marginBottom: "10px" }}>
                  Please type the property address in the Address Field and select from suggestions.
                </p>
                <Row className={rowClass}>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Security Position</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="securityPosition"
                        value={loanData.securityPosition}
                        setValue={handleFieldChange}
                        options={securityPositionOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Address</Label>
                    <div className="col-sm-6 position-relative">
                      <LoanField
                        label=""
                        name="propertyAddress"
                        type="text"
                        value={loanData.propertyAddress}
                        setValue={(name, val) => {
                          handleFieldChange(name, val);
                          fetchAddressSuggestions(val);
                        }}
                        passCol="col-12"
                      />
                      {addressSuggestions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          zIndex: 1000,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                        }}>
                          {addressSuggestions.map((item, idx) => (
                            <div
                              key={idx}
                              className="p-2 border-bottom"
                              style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                              onClick={() => {
                                handleFieldChange("propertyAddress", item.full_address || item.a);
                                setAddressSuggestions([]);
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              {item.full_address || item.a}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Estimated Value</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="estimatedValue"
                        value={loanData.estimatedValue}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Current Loan Balance</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="currentLoanBalance"
                        value={loanData.currentLoanBalance}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">LVR (%)</Label>
                    <div className="col-sm-6">
                      <LoanPercentField
                        label=""
                        name="lvr"
                        value={loanData.lvr}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={5}
                        disabled={true}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Property Class</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="propertyClass"
                        value={loanData.propertyClass}
                        setValue={handleFieldChange}
                        options={propertyClassOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>

                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">How will the loan be repaid?</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="repaymentPlan"
                        value={loanData.repaymentPlan}
                        setValue={handleFieldChange}
                        options={repaymentPlanOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">How long do you need loan for?</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                      label=""
                      name="loanTerm"
                      value={loanData.loanTerm}
                      setValue={handleFieldChange}
                      options={loanTermOptions}
                      passCol="col-12"
                    />
                    </div>
                  </Col>
                  {/* <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Repayment Type</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="repaymentType"
                        value={loanData.repaymentType}
                        setValue={handleFieldChange}
                        options={repaymentTypeOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col> */}
                </Row>
              </>
            )}



            {loanData.propertySecurityAvailable === "Secured - Asset/Equipment" && (
              <>
                <div style={sectionSeparatorStyle} />
                <h5 style={sectionTitleStyle}>SECURED - ASSET/EQUIPMENT</h5>
                <Row className={rowClass}>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Purchase Price</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="purchasePrice"
                        value={loanData.purchasePrice}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Cash Deposit</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="cashDeposit"
                        value={loanData.cashDeposit}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Trade in Value</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="tradeInValue"
                        value={loanData.tradeInValue}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Payout Amount</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="payoutAmount"
                        value={loanData.payoutAmount}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Preferred Loan Term</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="preferredLoanTerm"
                        value={loanData.preferredLoanTerm}
                        setValue={handleFieldChange}
                        options={assetLoanTermOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Balloon Residual %</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="balloonPayment"
                        value={loanData.balloonPayment}
                        setValue={handleFieldChange}
                        options={balloonResidualOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>

                  <Col md={12} className="mt-3 mb-2">
                    <h5 style={sectionTitleStyle}>ASSET TYPE</h5>
                  </Col>

                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">What Asset are you buying?</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="assetClass"
                        value={loanData.assetClass}
                        setValue={(name, val) => {
                          handleFieldChange(name, val);
                          handleFieldChange("assetSubClass", "");
                        }}
                        options={assetTypeOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>

                  {loanData.assetClass && assetSubClassMap[loanData.assetClass as keyof typeof assetSubClassMap] && (
                    <Col md={12} className="mb-1 d-flex align-items-center">
                      <Label className="col-sm-3 mb-0">Asset Sub Class</Label>
                      <div className="col-sm-6">
                        <LoanDropdown
                          label=""
                          name="assetSubClass"
                          value={loanData.assetSubClass}
                          setValue={handleFieldChange}
                          options={assetSubClassMap[loanData.assetClass as keyof typeof assetSubClassMap] || []}
                          passCol="col-12"
                        />
                      </div>
                    </Col>
                  )}

                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Asset Condition</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="assetType"
                        value={loanData.assetType}
                        setValue={handleFieldChange}
                        options={["New", "Old"]}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Asset year</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="assetYear"
                        value={loanData.assetYear}
                        setValue={handleFieldChange}
                        options={assetYears}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Asset source</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="assetSource"
                        value={loanData.assetSource}
                        setValue={handleFieldChange}
                        options={assetSourceOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Electric Vehicle</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="electricVehicle"
                        value={loanData.electricVehicle}
                        setValue={handleFieldChange}
                        options={electricVehicleOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}

            {loanData.propertySecurityAvailable === "Unsecured - Cashflow loan" && (
              <>
                <div style={sectionSeparatorStyle} />
                <h5 style={sectionTitleStyle}>UNSECURED - CASHFLOW LOAN</h5>
                <Row className={rowClass}>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">Monthly Turnover ($)</Label>
                    <div className="col-sm-6">
                      <LoanCurrencyField
                        label=""
                        name="monthlyTurnover"
                        value={loanData.monthlyTurnover}
                        setValue={handleFieldChange}
                        passCol="col-12"
                        limit={12}
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">How will the loan be repaid?</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="repaymentPlan"
                        value={loanData.repaymentPlan}
                        setValue={handleFieldChange}
                        options={repaymentPlanOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                  <Col md={12} className="mb-1 d-flex align-items-center">
                    <Label className="col-sm-3 mb-0">How long do you need loan for?</Label>
                    <div className="col-sm-6">
                      <LoanDropdown
                        label=""
                        name="loanTerm"
                        value={loanData.loanTerm}
                        setValue={handleFieldChange}
                        options={assetLoanTermOptions}
                        passCol="col-12"
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}


              <div style={sectionSeparatorStyle} />
              <h5 style={sectionTitleStyle}>DIRECTOR DETAILS</h5>
              <Row className={rowClass}>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Director 1 Name</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="director1"
                    type="text"
                    value={loanData.director1}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Director 1 Residency Status</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="director1Residency"
                    value={loanData.director1Residency}
                    setValue={handleFieldChange}
                    options={residencyStatusOptions}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Director 2 Name</Label>
                <div className="col-sm-6">
                  <LoanField
                    label=""
                    name="director2"
                    type="text"
                    value={loanData.director2}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Director 2 Residency Status</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="director2Residency"
                    value={loanData.director2Residency}
                    setValue={handleFieldChange}
                    options={residencyStatusOptions}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-1 d-flex align-items-center">
                <Label className="col-sm-3 mb-0">Has any Director been bankrupt?</Label>
                <div className="col-sm-6">
                  <LoanDropdown
                    label=""
                    name="directorBankrupt"
                    value={loanData.directorBankrupt}
                    setValue={handleFieldChange}
                    options={["Yes", "No"]}
                    passCol="col-12"
                  />
                </div>
              </Col>
              <Col md={12} className="mb-3 d-flex align-items-start">
                <Label className="col-sm-3 mb-0">Comments</Label>
                <div className="col-sm-9">
                  <LoanField
                    label=""
                    name="comments"
                    type="textarea"
                    rows={12}
                    value={loanData.comments}
                    setValue={handleFieldChange}
                    passCol="col-12"
                  />
                </div>
              </Col>
            </Row>

            <div className=" mt-5 mb-4">
              <Button
                color="success"
                size="lg"
                className="fw-bold px-5"
                style={{ backgroundColor: '#00a651', borderColor: '#00a651', borderRadius: '8px', padding: '15px 30px', fontSize: '1.25rem' }}
                disabled={isLoading || !hasLeadId}
                onClick={handleViewResults}
              >
                Get Your Lender Matches Here
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoanEnquiry;
